// src/pages/materias/laboral/components/ChecklistPrejudicialCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { notify } from "../../ui/notify";
import {
  Card,
  Divider,
  Typography,
  Space,
  Button,
  Form,
  Upload,
  Spin,
  Modal,
} from "antd";
import { HappyProvider } from "@ant-design/happy-work-theme";
import ReactQuill from "react-quill";
// Iconografía: bootstrap-icons en vez de @ant-design/icons.
import { useDispatch, useSelector } from "react-redux";

import {
  actionConciliacionDocumentosGet,
  actionConciliacionGetByID,
} from "../../../redux/actions/conciliacion/conciliacion";
import {PATH} from "../../../redux/utils"
const API_BASE = PATH;
const { Text } = Typography;
const { Dragger } = Upload;

// ID del tipo de documento "Checklist prejudicial"
const CHECKLIST_TIPO_ID = 2;

// Extensiones permitidas para el checklist prejudicial (PDF, Word, texto e imágenes)
const CHECKLIST_ALLOWED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "odt",
  "txt",
  "csv",
  "rtf",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "webp",
  "tiff",
  "tif",
];
const CHECKLIST_ACCEPT = CHECKLIST_ALLOWED_EXTENSIONS.map(
  (ext) => `.${ext}`
).join(",");

export default function ChecklistPrejudicialCard({ idExpediente }) {
  const dispatch = useDispatch();
  const { documentos, detalle } = useSelector((state) => state.conciliacion);

  // =========================
  //  ESTADO PROPUESTA PATRÓN
  // =========================
  const [propuestaPatron, setPropuestaPatron] = useState("");
  // "summary" = muestra bloque resumen, "edit" = muestra formulario
  const [propuestaMode, setPropuestaMode] = useState("edit");
  const [savingChecklistData, setSavingChecklistData] = useState(false);
  const [formChecklist] = Form.useForm();

  // =========================
  //  ESTADO CHECKLIST (ARCHIVOS)
  // =========================
  // Cuántas subidas están en curso simultáneamente (permite varias a la vez)
  const [uploadingCount, setUploadingCount] = useState(0);
  const checklistUploading = uploadingCount > 0;

  // ---------------------------------
  // Inicializar propuesta desde detalle
  // ---------------------------------
  useEffect(() => {
    if (!detalle) return;

    const value =
      typeof detalle.propuesta_patron === "string"
        ? detalle.propuesta_patron
        : "";

    setPropuestaPatron(value);
    formChecklist.setFieldsValue({ propuesta_patron: value });

    // Si hay texto en BD => modo resumen, si no => modo edición
    if (value.trim()) {
      setPropuestaMode("summary");
    } else {
      setPropuestaMode("edit");
    }
  }, [detalle, formChecklist]);

  // --------------------------
  // Lista de documentos del checklist (todos los activos, no solo uno)
  // --------------------------
  const checklistDocs = useMemo(() => {
    const items = documentos?.items?.items;
    if (!Array.isArray(items)) return [];

    return items.filter(
      (doc) =>
        Number(doc.id_conciliacion_tipo_documento) === CHECKLIST_TIPO_ID &&
        Number(doc.active) === 1
    );
  }, [documentos]);

  // =========================
  //  HANDLERS PROPUESTA
  // =========================

  const handleChecklistDataSubmit = async (values) => {
    try {
      const expedienteVal = detalle?.expediente_format;

      if (!expedienteVal) {
        notify.error({
          message: "Expediente no disponible",
          description:
            "No se encontró el expediente para guardar la propuesta del patrón.",
        });
        return;
      }

      const payload = {
        expediente: expedienteVal,
        propuesta_patron: values.propuesta_patron || null,
      };

      setSavingChecklistData(true);

      const res = await fetch(
        `${PATH}/checklist-prejudicial/datos`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const json = await res.json();

      if (!res.ok || json.status !== "ok") {
        console.error("Error backend (datos checklist):", json);
        notify.error({
          message: "Error al guardar",
          description:
            "No se pudieron guardar los datos del checklist (propuesta del patrón).",
        });
        setSavingChecklistData(false);
        return;
      }

      const newValue = values.propuesta_patron || "";
      setPropuestaPatron(newValue);
      // tras guardar, volvemos a modo resumen; si está vacío, se muestra placeholder
      setPropuestaMode("summary");

      dispatch(actionConciliacionGetByID(idExpediente));

      notify.success({
        message: "Datos guardados",
        description: "La propuesta del patrón se guardó correctamente.",
      });
      setSavingChecklistData(false);
    } catch (err) {
      console.error(err);
      notify.error({
        message: "Error de conexión",
        description:
          "Ocurrió un error de conexión al guardar la propuesta del patrón.",
      });
      setSavingChecklistData(false);
    }
  };

  const handleEditChecklistData = () => {
    setPropuestaMode("edit");
    formChecklist.setFieldsValue({
      propuesta_patron: propuestaPatron || "",
    });
  };

  // =========================
  //  HANDLERS CHECKLIST (ARCHIVOS)
  // =========================

  const handleChecklistBeforeUpload = (file) => {
    const ext = file.name?.split(".").pop()?.toLowerCase();

    if (!ext || !CHECKLIST_ALLOWED_EXTENSIONS.includes(ext)) {
      notify.error({
        message: "Formato no permitido",
        description:
          "Solo se permiten archivos PDF, Word (doc/docx/odt/rtf), TXT, CSV o imágenes (jpg, png, gif, bmp, webp, tiff).",
      });
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleChecklistUpload = async ({ file, onSuccess, onError }) => {
    setUploadingCount((c) => c + 1);
    try {
      if (!detalle?.expediente_format) {
        notify.error({
          message: "Datos incompletos",
          description:
            "Falta el expediente_format en los datos del expediente.",
        });
        onError?.();
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("expediente", String(detalle.expediente_format));

      const res = await fetch(
         `${PATH}/checklist-prejudicial/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      const json = await res.json();

      if (!res.ok || json.status !== "ok") {
        console.error("Error backend:", json);
        notify.error({
          message: "Error al importar",
          description: `No se pudo importar "${file.name}".`,
        });
        onError?.(json);
        return;
      }

      notify.success({
        message: "Checklist importado",
        description: `"${file.name}" se importó correctamente.`,
      });

      onSuccess?.(json);

      // refrescamos documentos para que llegue la lista actualizada
      dispatch(actionConciliacionDocumentosGet(idExpediente));
    } catch (err) {
      console.error(err);
      notify.error({
        message: "Error de conexión",
        description:
          "No se pudo conectar con el servidor al importar el checklist.",
      });
      onError?.(err);
    } finally {
      setUploadingCount((c) => Math.max(0, c - 1));
    }
  };

  const handleViewChecklistDoc = (doc) => {
    if (!doc?.id_conciliacion || !doc?.path) {
      notify.error({
        message: "Documento no disponible",
        description: "No se encontró la ruta del archivo para visualizarlo.",
      });
      return;
    }

    const url = `${API_BASE}/uploads/conciliaciones/${doc.id_conciliacion}/${doc.path}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDeleteChecklistDoc = (doc) => {
    const expedienteVal = detalle?.expediente_format;

    if (!expedienteVal || !doc?.path) {
      notify.error({
        message: "Checklist no disponible",
        description: "No se encontró la información del checklist.",
      });
      return;
    }

    Modal.confirm({
      title: "Eliminar documento",
      content: `Se eliminará "${
        doc.filename || doc.path
      }". ¿Deseas continuar?`,
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      async onOk() {
        try {
          const res = await fetch(
             `${PATH}/checklist-prejudicial/delete`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                expediente: expedienteVal,
                fileName: doc.path,
              }),
            }
          );

          const json = await res.json();

          if (!res.ok || json.status !== "ok") {
            console.error("Error backend (delete checklist):", json);
            notify.error({
              message: "Error al eliminar",
              description: "No se pudo eliminar el documento.",
            });
            return;
          }

          dispatch(actionConciliacionGetByID(idExpediente));
          dispatch(actionConciliacionDocumentosGet(idExpediente));

          notify.success({
            message: "Documento eliminado",
            description: "El documento se eliminó correctamente.",
          });
        } catch (err) {
          console.error(err);
          notify.error({
            message: "Error de conexión",
            description:
              "Ocurrió un error de conexión al eliminar el documento.",
          });
        }
      },
    });
  };

  // =========================
  //  RENDER
  // =========================

  return (
    <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2">
      <div className="laboral-exp-checklist-panel laboral-exp-checklist-panel--visible">
        <div className="laboral-exp-section-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="laboral-exp-icon-badge">
              <i className="fas fa-clipboard-list" />
            </div>
            <div>
              <Text className="laboral-exp-section-title">
                Checklist prejudicial
              </Text>
              <Text type="secondary" className="laboral-exp-section-sub">
                Puedes capturar la propuesta del patrón aunque no se haya cargado
                el checklist. Opcionalmente, también puedes subir uno o varios
                documentos cuando los tengas.
              </Text>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            {propuestaMode === "summary" && (
              <Button size="small" onClick={handleEditChecklistData}>
                Editar datos
              </Button>
            )}
          </div>
        </div>

        <Divider className="laboral-exp-section-divider" />

        <div className="laboral-exp-checklist-grid">
          {/* ==================
              IZQUIERDA: PROPUESTA
             ================== */}
          <div className="laboral-exp-checklist-form">
            {propuestaMode === "summary" ? (
              <div className="laboral-exp-checklist-summary">
                <div className="laboral-exp-checklist-summary-title">
                  <Text strong>Datos capturados del checklist</Text>
                </div>

                <div className="laboral-exp-checklist-summary-items">
                  <div className="laboral-exp-checklist-item">
                    <span className="laboral-exp-checklist-item-label">
                      PROPUESTA DEL PATRÓN
                    </span>
                    <div
                      className="laboral-exp-checklist-item-value"
                      dangerouslySetInnerHTML={{
                        __html: propuestaPatron?.trim()
                          ? propuestaPatron
                          : "<em>Sin propuesta registrada</em>",
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <Form
                form={formChecklist}
                layout="vertical"
                onFinish={handleChecklistDataSubmit}
              >
                <Form.Item
                  colon={false}
                  label={
                    <span className="laboral-exp-checklist-form-label">
                      <span className="laboral-exp-checklist-form-label-title">
                        Propuesta del patrón
                      </span>
                      <span className="laboral-exp-checklist-form-label-hint">
                        Escribe aquí, en tus palabras, lo que propuso el patrón durante la audiencia (monto, condiciones, plazos, etc.)
                      </span>
                    </span>
                  }
                  name="propuesta_patron"
                >
                  <ReactQuill
                    theme="snow"
                    style={{ height: 180, marginBottom: 40 }}
                  />
                </Form.Item>
                <Form.Item>
                  <Space>
                    <HappyProvider>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={savingChecklistData}
                        icon={<i className="fa-solid fa-floppy-disk" />}
                      >
                        Guardar
                      </Button>
                    </HappyProvider>
                  </Space>
                </Form.Item>
              </Form>
            )}
          </div>

          {/* ==================
              DERECHA: ARCHIVOS CHECKLIST
             ================== */}
          <div className="laboral-exp-checklist-preview">
            <div className="laboral-exp-pdf-preview-wrapper laboral-exp-pdf-preview-wrapper--compact">
              <Dragger
                name="file"
                multiple
                showUploadList={false}
                accept={CHECKLIST_ACCEPT}
                beforeUpload={handleChecklistBeforeUpload}
                customRequest={handleChecklistUpload}
                className="laboral-exp-checklist-dragger"
              >
                {checklistUploading ? (
                  <div style={{ padding: "12px 0" }}>
                    <Spin />
                    <p
                      className="laboral-exp-checklist-dragger-title"
                      style={{ marginTop: 12 }}
                    >
                      Importando checklist...
                    </p>
                    <p className="laboral-exp-checklist-dragger-sub">
                      Espera un momento mientras procesamos el o los
                      archivos.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="ant-upload-drag-icon">
                      <i className="bi bi-inbox" />
                    </p>
                    <p className="laboral-exp-checklist-dragger-title">
                      Haz clic o arrastra uno o varios archivos para importar
                      el checklist
                    </p>
                    <p className="laboral-exp-checklist-dragger-sub">
                      Se permiten archivos PDF, Word (doc, docx, odt, rtf),
                      TXT, CSV o imágenes (jpg, png, gif, bmp, webp, tiff).
                      Estos documentos se usarán para llenar el detalle del
                      acuerdo, pero son opcionales para guardar la propuesta
                      del patrón.
                    </p>
                  </>
                )}
              </Dragger>

              <div className="laboral-exp-checklist-doc-list">
                {checklistDocs.length === 0 ? (
                  <Text type="secondary" className="laboral-exp-checklist-doc-empty">
                    Aún no se han cargado documentos del checklist.
                  </Text>
                ) : (
                  checklistDocs.map((doc) => (
                    <div key={doc.id} className="laboral-exp-checklist-doc-row">
                      <div className="laboral-exp-checklist-doc-row-info">
                        <i className="bi bi-file-earmark-pdf laboral-exp-checklist-doc-row-icon" />
                        <Text
                          className="laboral-exp-checklist-doc-row-name"
                          ellipsis={{ tooltip: doc.filename || doc.path }}
                        >
                          {doc.filename || doc.path}
                        </Text>
                      </div>
                      <Space size={4}>
                        <Button
                          size="small"
                          type="text"
                          icon={<i className="bi bi-eye" />}
                          onClick={() => handleViewChecklistDoc(doc)}
                        >
                          Ver
                        </Button>
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<i className="bi bi-trash" />}
                          onClick={() => handleDeleteChecklistDoc(doc)}
                        >
                          Eliminar
                        </Button>
                      </Space>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
